import { Link } from "@kaltura/ds-react-components";
import { TreeView, TreeItemProps } from "@kaltura/ds-react-tree";
import { StyledFolderIcon } from "../StyledComponents";
import { SyntheticEvent, useMemo, useState, DragEvent } from "react";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";
import { ToastMessages, TruncatedLine } from "@kaltura/mediaspace-shared-ui";
import { useFoldersStore } from "../foldersStore";
import { UNFILED_FOLDER_ID, UpdateMediaFoldersRequestParams, UpdateMediaFoldersResponseParams } from "../types";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import RemoveFromAllFoldersModal from "../remove-from-all-folders/RemoveFromAllFoldersModal";
import { ButtonClickAnalyticsType, FilterValues } from "@kaltura/mediaspace-shared-types";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface FoldersFiltersProps {
    /** currently selected folder id in the filter */
    selectedFolderId: string;
    /** callback to execute when selecting a folder id filter */
    onChange: (values: FilterValues) => void;
}

const StyledExpandButton = styled(Link)(({ theme }) => ({
    marginTop: theme.spacing(0.5),
}));

const MAX_COLLAPSED_ITEMS = 8;

export function FoldersFilters({ selectedFolderId, onChange }: FoldersFiltersProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isRemoveFromAllModalOpen, setIsRemoveFromAllModalOpen] = useState(false);
    const [entryIdsToRemove, setEntryIdsToRemove] = useState<string[]>([]);

    const foldersTreeItems = useFoldersStore((state) => state.foldersTreeItems);
    const foldersMap = useFoldersStore((state) => state.foldersMap);

    const sendButtonAnalytics = useButtonAnalytics();
    const { showToast } = useToastsContext();

    const handleSelect = (_event: SyntheticEvent, itemId: string | null) => {
        onChange({ folderId: itemId === selectedFolderId ? "" : itemId ?? "" });
        sendButtonAnalytics("Select folder in tree", ButtonClickAnalyticsType.NAVIGATE);
    };

    const toggleIsExpanded = () => {
        sendButtonAnalytics(
            isExpanded ? "View less folders" : "View all folders",
            isExpanded ? ButtonClickAnalyticsType.COLLAPSE : ButtonClickAnalyticsType.EXPAND
        );
        setIsExpanded(!isExpanded);
    };

    const handleOpenRemoveFromAllModal = () => {
        setIsRemoveFromAllModalOpen(true);
    };

    const handleCloseRemoveFromAllModal = () => {
        setIsRemoveFromAllModalOpen(false);
    };

    const reloadMedia = () => {
        // trigger reloading media by changing filterValues reference
        onChange({ folderId: selectedFolderId });
    };

    const handleRemoveFromAllEnd = () => {
        setEntryIdsToRemove([]);
        handleCloseRemoveFromAllModal();
        reloadMedia();
    };

    const handleDropOnItem = async (e: DragEvent<HTMLDivElement>, droppedOnId: string) => {
        const data = e.dataTransfer.getData("application/json");
        if (data) {
            try {
                const parsedData = JSON.parse(data);
                if (parsedData?.type === "entries" && Array.isArray(parsedData?.ids)) {
                    const draggedEntryIds = parsedData.ids;
                    // report button analytics for drop
                    sendButtonAnalytics(
                        "Add to folder - Drag N Drop",
                        ButtonClickAnalyticsType.ADD,
                        draggedEntryIds.length === 1 ? draggedEntryIds[0] : undefined
                    );
                    // drop on unfiled media should remove media from all folders
                    if (droppedOnId === UNFILED_FOLDER_ID) {
                        handleOpenRemoveFromAllModal();
                        setEntryIdsToRemove(draggedEntryIds);
                    }
                    // add (copy) dragged content to the folder it was dropped on
                    else {
                        try {
                            const requestData: UpdateMediaFoldersRequestParams = {
                                entryIds: draggedEntryIds,
                                addFolderIds: [droppedOnId],
                                removeFolderIds: [],
                            };
                            const result: UpdateMediaFoldersResponseParams = await postKmsData(
                                `${baseUrl}/folders/entry/update`,
                                requestData
                            );
                            if (result.successMessages && result.successMessages.length > 0) {
                                // max one success message is returned (add OR remove from folder)
                                showToast({
                                    severity: "success",
                                    message: result.successMessages[0],
                                });
                                // refresh content if unfiled media is currently selected in filter
                                if (selectedFolderId === UNFILED_FOLDER_ID) {
                                    reloadMedia();
                                }
                            }
                            // the server returns either successMessages or errorMessages - not both
                            else {
                                if (result.errorMessages && result.errorMessages.length > 0) {
                                    showToast({
                                        severity: "error",
                                        message: (
                                            <ToastMessages
                                                title={translate("Failed to add the following media:")}
                                                messages={result.errorMessages}
                                            />
                                        ),
                                    });
                                }
                            }
                        }
                        catch (e) {
                            showToast({
                                severity: "error",
                                message: translate("Failed to add media to folders"),
                            });
                        }
                    }
                }
            }
            catch (err) {
                console.error("Failed to parse dragged data:", err);
            }
        }
    };

    const unfiledMedia = useMemo<TreeItemProps>(
        () => ({
            id: UNFILED_FOLDER_ID,
            label: <TruncatedLine>{translate("Unfiled media")}</TruncatedLine>,
        }),
        []
    );

    const allFoldersFilters = useMemo(() => [unfiledMedia, ...foldersTreeItems], [unfiledMedia, foldersTreeItems]);

    const defaultExpanded =
        selectedFolderId === "" || selectedFolderId === UNFILED_FOLDER_ID
            ? []
            : foldersMap.get(selectedFolderId)?.fullPathIds.slice(0, -1);

    // ensure correct defaultExpanded is available
    // so TreeView will receive it on initial render
    return defaultExpanded ? (
        <>
            <TreeView
                items={!isExpanded ? allFoldersFilters.slice(0, MAX_COLLAPSED_ITEMS) : allFoldersFilters}
                defaultStartIcon={<StyledFolderIcon />}
                iconExpansionOnly={true}
                selectedItems={selectedFolderId}
                onSelectedItemsChange={handleSelect}
                defaultExpandedItems={defaultExpanded}
                onItemDrop={handleDropOnItem}
            />
            {allFoldersFilters.length > MAX_COLLAPSED_ITEMS && (
                <StyledExpandButton
                    component={"button"}
                    size={"small"}
                    onClick={toggleIsExpanded}
                    aria-expanded={isExpanded}
                >
                    {!isExpanded ? translate("View all folders") : translate("View less folders")}
                </StyledExpandButton>
            )}
            <ButtonAnalyticsProvider location={"Modal - Remove from all folders"} applicationFeature={"folders"}>
                <RemoveFromAllFoldersModal
                    isOpen={isRemoveFromAllModalOpen}
                    onClose={handleCloseRemoveFromAllModal}
                    entryIds={entryIdsToRemove}
                    onSubmitEnd={handleRemoveFromAllEnd}
                />
            </ButtonAnalyticsProvider>
        </>
    ) : null;
}

export default FoldersFilters;
